import {
    EVERYONE_SENTINEL,
    minimumReferenceSearchLength,
    PromptId,
    referenceSearchQueryTerm,
} from "@sp-crm/core";
import { countFiltersApplied } from "components/advanced-search/advanced-search-utility";
import { SearchGeoParamsCallout } from "components/community-search/search-bar/search-geo-params-callout";
import { useDynamicCalloutPosition } from "components/community-search/search-bar/use-dynamic-callout-position";
import { useDebouncedState } from "components/shared/hooks";
import { SelectableUser, UserSelect } from "components/shared/user-select";
import { defaultLinkStyle } from "components/ui/link";
import { SearchInput } from "components/ui/search-input";
import {
    AdvancedSearchCondition,
    LayoutSectionParentKey,
    SearchGeoParams,
} from "generated/graphql";
import React, { useCallback, useEffect, useMemo } from "react";
import { useCanSeeReferralSourceOwners, useFeature } from "store/selectors/hooks";
import { ReferenceFilter } from "store/slices/reference-dashboard";
import { BestDaysToVisitFilter } from "./best-days-to-visit-filter";
import { EntityMoreFilter, EntityMoreFilterValue } from "./entity-more-filter";
import { OrgTypeFilter } from "./org-type-filter";
import { ReferralTierFilter } from "./referral-tier-filter";

interface ReferenceSearchBarProps {
    value: ReferenceFilter;
    onChange: (filter: ReferenceFilter) => void;
    onResetMapView?: () => void;
    layout: "inline" | "stacked";
}

export const ReferenceSearchBar: React.FC<ReferenceSearchBarProps> = props => {
    const assignedToVisible = useCanSeeReferralSourceOwners();
    const bestDaysToVisitFilterEnabled = useFeature("detailsPageVNext");
    const { value, onChange, onResetMapView, layout } = props;
    const isStacked = layout === "stacked";

    const [geoParamsRef, geoParamsPosition] = useDynamicCalloutPosition();

    const [searchTerm, setSearchTerm, searchTermDebounced] = useDebouncedState(
        value.search,
        400,
    );

    useEffect(() => {
        const effectiveSearch = referenceSearchQueryTerm(searchTermDebounced);
        if (effectiveSearch !== value.search) {
            onChange({
                ...value,
                search: effectiveSearch,
            });
        }
    }, [searchTermDebounced]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleOrgTypeChanged = (orgTypes: string[]) => {
        onChange({
            ...value,
            orgTypes,
        });
    };

    const handleCommunityTypesChanged = (communityTypes: PromptId[]) => {
        onChange({
            ...value,
            communityTypes,
        });
    };

    const handleReferralTierChanged = (referralTierIds: string[]) => {
        onChange({
            ...value,
            referralTierIds,
        });
    };

    const handleBestDaysToVisitChanged = (bestDaysToVisit: string[]) => {
        onChange({
            ...value,
            bestDaysToVisit,
        });
    };

    const handleClearOrgTypeFilter = () => {
        onChange({
            ...value,
            orgTypes: [],
            communityTypes: [],
        });
    };

    const handleAssignedUserChanged = (assignedUserId: SelectableUser) => {
        onChange({
            ...value,
            assignedUserId,
        });
    };

    const handleContactConditionChanged = useCallback(
        (condition: AdvancedSearchCondition | null) => {
            onChange({
                ...value,
                contactCondition: condition,
            });
        },
        [onChange, value],
    );

    const handleBusinessConditionChanged = useCallback(
        (condition: AdvancedSearchCondition | null) => {
            onChange({
                ...value,
                businessCondition: condition,
            });
        },
        [onChange, value],
    );

    const handleCommunityConditionChanged = useCallback(
        (condition: AdvancedSearchCondition | null) => {
            onChange({
                ...value,
                communityCondition: condition,
            });
        },
        [onChange, value],
    );

    const searchTermTooShort =
        searchTerm.trim().length > 0 &&
        referenceSearchQueryTerm(searchTerm) === "" &&
        searchTermDebounced.trim().length > 0 &&
        referenceSearchQueryTerm(searchTermDebounced) === "";

    const showClearButton = useMemo(() => {
        return (
            searchTerm.trim().length > 0 ||
            (value.search?.length ?? 0) > 0 ||
            (value.orgTypes?.length ?? 0) > 0 ||
            (value.communityTypes?.length ?? 0) > 0 ||
            (value.referralTierIds?.length ?? 0) > 0 ||
            (value.bestDaysToVisit?.length ?? 0) > 0 ||
            countFiltersApplied(value.contactCondition) > 0 ||
            countFiltersApplied(value.businessCondition) > 0 ||
            countFiltersApplied(value.communityCondition) > 0 ||
            value.assignedUserId !== EVERYONE_SENTINEL
        );
    }, [
        searchTerm,
        value.assignedUserId,
        value.search,
        value.orgTypes,
        value.communityTypes,
        value.referralTierIds,
        value.bestDaysToVisit,
        value.contactCondition,
        value.businessCondition,
        value.communityCondition,
    ]);

    const handleGeoParamsChanged = (geoParams: SearchGeoParams | null) => {
        onChange({
            ...value,
            geoParams,
        });
    };

    const handleEntityConditionChanged = useCallback(
        (entityType: string, condition: AdvancedSearchCondition | null) => {
            if (entityType === "ReferenceContact") {
                handleContactConditionChanged(condition);
            } else if (entityType === "ReferenceBusiness") {
                handleBusinessConditionChanged(condition);
            } else if (entityType === "Community") {
                handleCommunityConditionChanged(condition);
            }
        },
        [
            handleBusinessConditionChanged,
            handleCommunityConditionChanged,
            handleContactConditionChanged,
        ],
    );

    const conditionConfigs: Record<string, EntityMoreFilterValue> = useMemo(() => {
        return {
            ReferenceContact: {
                condition: value.contactCondition,
                onChange: handleContactConditionChanged,
                label: "Contact filters",
                icon: "40_ReferralPerson",
                entityType: "ReferenceContact",
                layoutContainerKeys: [LayoutSectionParentKey.ReferenceContact],
            },
            ReferenceBusiness: {
                condition: value.businessCondition,
                onChange: handleBusinessConditionChanged,
                label: "Referral Organization filters",
                icon: "39_Business",
                entityType: "ReferenceBusiness",
                layoutContainerKeys: [LayoutSectionParentKey.ReferenceBusiness],
            },
            Community: {
                condition: value.communityCondition,
                onChange: handleCommunityConditionChanged,
                label: "Community filters",
                icon: "57_Community",
                entityType: "Community",
                layoutContainerKeys: [
                    LayoutSectionParentKey.CommunityDetailsColumn1,
                    LayoutSectionParentKey.CommunityDetailsColumn2,
                ],
            },
        };
    }, [
        value.contactCondition,
        value.businessCondition,
        value.communityCondition,
        handleBusinessConditionChanged,
        handleCommunityConditionChanged,
        handleContactConditionChanged,
    ]);

    return (
        <div
            className={
                isStacked
                    ? "space-y-4"
                    : "max-2xl:space-y-4 2xl:flex 2xl:items-end 2xl:space-x-2"
            }>
            <div className="max-sm:space-y-4 sm:flex sm:items-end sm:space-x-2">
                <div className={isStacked ? "flex-1" : "2xl:w-80 max-2xl:flex-1"}>
                    <SearchInput
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        placeholder="Organization or contact name"
                    />
                    {searchTermTooShort ? (
                        <p className="text-sm text-gray-500">
                            Type at least {minimumReferenceSearchLength} characters to
                            search
                        </p>
                    ) : null}
                </div>
                <div className={isStacked ? "flex-1" : "max-2xl:flex-1"}>
                    <UserSelect
                        label="Filter by assigned to"
                        onChange={handleAssignedUserChanged}
                        value={value.assignedUserId}
                        includeEveryone
                        restricted={!assignedToVisible}
                    />
                </div>
            </div>
            <div className="gap-2 max-lg:grid max-lg:grid-cols-2 lg:flex">
                <div className="lg:pb-2" ref={geoParamsRef}>
                    {onResetMapView ? (
                        <SearchGeoParamsCallout
                            value={value.geoParams}
                            onChange={handleGeoParamsChanged}
                            onResetMapView={onResetMapView}
                            align={geoParamsPosition.align}
                            arrowPosition={geoParamsPosition.arrowPosition}
                            maxWidthPx={geoParamsPosition.maxWidthPx}
                            mobileFullWidth={geoParamsPosition.mobileFullWidth}
                        />
                    ) : null}
                </div>
                <div className="lg:pb-2">
                    <OrgTypeFilter
                        value={value.orgTypes}
                        onChange={handleOrgTypeChanged}
                        communityTypes={value.communityTypes}
                        onCommunityTypesChange={handleCommunityTypesChanged}
                        onClear={handleClearOrgTypeFilter}
                    />
                </div>
                <div className="lg:pb-2">
                    <ReferralTierFilter
                        value={value.referralTierIds}
                        onChange={handleReferralTierChanged}
                    />
                </div>
                {bestDaysToVisitFilterEnabled ? (
                    <div className="lg:pb-2">
                        <BestDaysToVisitFilter
                            value={value.bestDaysToVisit}
                            onChange={handleBestDaysToVisitChanged}
                        />
                    </div>
                ) : null}
                <div className="lg:pb-2">
                    <EntityMoreFilter
                        conditions={[conditionConfigs.ReferenceContact]}
                        onConditionChange={handleEntityConditionChanged}
                        label="Contact filters"
                    />
                </div>
                <div className="lg:pb-2">
                    <EntityMoreFilter
                        conditions={[
                            conditionConfigs.ReferenceBusiness,
                            conditionConfigs.Community,
                        ]}
                        onConditionChange={handleEntityConditionChanged}
                        label="Organization filters"
                    />
                </div>
                {showClearButton ? (
                    <a
                        href="#"
                        className={`${defaultLinkStyle} text-sm lg:pb-2`}
                        onClick={e => {
                            e.preventDefault();
                            setSearchTerm("");
                            onChange({
                                search: "",
                                orgTypes: [],
                                communityTypes: [],
                                referralTierIds: [],
                                bestDaysToVisit: [],
                                contactCondition: null,
                                businessCondition: null,
                                communityCondition: null,
                                assignedUserId: EVERYONE_SENTINEL,
                                geoParams: null,
                            });
                        }}>
                        Clear all filters
                    </a>
                ) : null}
            </div>
        </div>
    );
};
